import * as React from 'react';
import { cva } from 'class-variance-authority';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CalloutProps {
  tone: 'info' | 'success' | 'warning' | 'danger';
  title?: string;
  icon?: LucideIcon;
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

const calloutVariants = cva(
  'flex items-start gap-inset-sm rounded-md border p-inset text-body-s',
  {
    variants: {
      tone: {
        info: 'border-info bg-info-subtle text-fg',
        success: 'border-success bg-success-subtle text-fg',
        warning: 'border-warning bg-warning-subtle text-fg',
        danger: 'border-danger bg-danger-subtle text-fg',
      },
    },
  },
);

const calloutIconVariants = cva('size-icon-md shrink-0', {
  variants: {
    tone: {
      info: 'text-info',
      success: 'text-success',
      warning: 'text-warning',
      danger: 'text-danger',
    },
  },
});

/**
 * A default icon for each `tone`, different in silhouette (not only in colour):
 * that way "nothing communicates by colour alone" (WCAG 1.4.1) holds even when
 * whoever consumes `Callout` passes no `icon`. If they do pass one, that icon
 * rules.
 */
const DEFAULT_ICON: Record<CalloutProps['tone'], LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle,
};

/**
 * The inline notice primitive (9 screens, see
 * `docs/FRONTEND_COMPONENT_INVENTORY.md` §2.2). It knows no domain: the specific
 * uses (`GlosaCallout`, `FoliosWarningCallout`, …) compose it.
 */
export function Callout({
  tone,
  title,
  icon,
  children,
  action,
  className,
}: CalloutProps): React.JSX.Element {
  const Icon = icon ?? DEFAULT_ICON[tone];

  return (
    <div className={cn(calloutVariants({ tone }), className)}>
      <Icon aria-hidden="true" className={cn(calloutIconVariants({ tone }))} />
      <div className="flex flex-1 flex-col gap-stack-sm">
        {title ? <p className="font-semibold">{title}</p> : null}
        <div>{children}</div>
        {action ? <div>{action}</div> : null}
      </div>
    </div>
  );
}
